/**
 * Platform super-admin god-view — cross-org / cross-user read endpoints
 * (multi-tenancy Phase E). Gated server-side by RequirePlatformAdmin
 * (users.is_platform_admin); NOT org-scoped. Surfaced in the SPA only for
 * useOrg().isPlatformAdmin users (the /app/platform console).
 */

import { api } from "@/lib/api-client";
import { fetchAllPages } from "./paginate";
import type { Paginated } from "./types";

/** What an org's members see of its knowledge store with the switch on, and what the Sahas team holds back
 *  (GET /v1/admin/orgs/{org_id}/knowledge-visible; knowledge.CountCustomerView). */
export interface KnowledgeCustomerView {
  org_id: string;
  knowledge_customer_visible: boolean;
  customer_sees: {
    findings: number;
    questions: number;
    held_back: { candidates: number; data_quality: number; internal: number; draft_questions: number };
  };
}

/** Mirrors admin.OrgSummary. */
export interface OrgSummary {
  id: string;
  slug: string;
  name: string;
  created_at: string;
  member_count: number;
  is_disabled: boolean;
  /** foundry pack enabled → Tier-2 digital twin surface is on. */
  twin_enabled: boolean;
  /**
   * Which genealogy model the org's latest plant runs on: "new" when
   * plants.source='newmodel' (the mass-conserving / time-stamped emitter +
   * the new UI via useNewModel), else "legacy" (today's demo emitter, incl.
   * orgs with no provisioned plant). Drives the console's per-org
   * genealogy-model toggle.
   */
  genealogy_model: "new" | "legacy";
  /**
   * Per-org physics-twin opt-in (org_settings.physics_twin_enabled) — gates
   * the S2 live physics tick, the reconcile worker and /v1/twin/simcore/*.
   * Default OFF. Drives the console's per-org physics-twin toggle.
   */
  physics_twin_enabled: boolean;
  /**
   * Per-org demo-simulator kill switch (org_settings.simulator_enabled,
   * migration 0092, #581). DEFAULT true (opt-out), so an org with no settings
   * row reads as enabled. false means the simulator writes no new readings for
   * this org — the per-org alternative to the global SIMULATOR_DISABLED flag.
   * Drives the console's per-org "Simulate" toggle.
   */
  simulator_enabled: boolean;
  /**
   * Per-org analysis plan view (org_settings.analysis_plan_view, migration 0169, plant-analytics S14):
   * shows the Plan tab of the correlations page. Default OFF; an org with no settings row reads as off.
   * Drives the console's per-org "Plan view" toggle.
   */
  analysis_plan_view: boolean;
  /**
   * The knowledge store's per-org customer switch (org_settings.knowledge_customer_visible, migration 0156,
   * plant-analytics B-1 and B-6b): the org's members see its confirmed findings and the Knowledge page opens
   * for them. Default OFF; an org with no settings row reads as off. Drives the console's "Knowledge" toggle.
   */
  knowledge_customer_visible: boolean;
  /**
   * A reader's own revision (org_settings.auto_revise_readers, migration 0260; R2 of
   * docs/plans/2026-10-19_reader-layout-change.md): an approved reader that matched one of the org's files and then
   * failed on it opens a revision of itself on that file. Default OFF. Optional: an older API build does not send it,
   * and reads as off. Drives the console's "Readers" toggle.
   */
  auto_revise_readers?: boolean;
  /**
   * The platform's gate for the org's improvement projects (org_settings.improvement_projects_allowed, migration 0200,
   * docs/plans/2026-10-05_improvement-projects.md): open, the org's admins may turn projects on; shut (the default, and an
   * org with no settings row), their switch is not shown and every project route is off. Optional: an older API build does
   * not send it, and reads as shut. The API process's IMPROVEMENT_PROJECTS_ENABLED opens it to every org whatever this says.
   * Drives the console's "Projects" toggle.
   */
  improvement_projects_allowed?: boolean;
  /**
   * Classifies the org by the provenance of its equipment: "real" (has
   * real/imported things, none simulated), "simulated" (all its things are
   * factory-minted / demo), "mixed" (both), or "empty" (no equipment). Keyed
   * on things.is_simulated — the same flag the sim-cleanup / factory purge
   * key on. Lets the operator tell dummy demo orgs from real tenants at a
   * glance; drives the console's Real/Sim badge + the dummy→real toggle.
   */
  data_class: "real" | "simulated" | "mixed" | "empty";
}


/** Where a limit of the AI Casting Consultant comes from: the code's default, the server's environment, or the org's own. */
export type CopilotLimitSource = "default" | "env" | "org";

/** One daily limit of the consultant as it applies to an org (twin.CopilotLimit, migration 0201). */
export interface CopilotLimit {
  /** The messages allowed in any 24 hours. */
  limit: number;
  source: CopilotLimitSource;
  /** The platform's limit, which applies when the org has none of its own. */
  platform: number;
  platform_source: "default" | "env";
}

/** One person's messages counted against their daily limit (twin.CopilotUsage). */
export interface CopilotUsage {
  user_id: string;
  email: string;
  display_name: string;
  count: number;
  /** Where the count starts: a day ago, or the latest reset that applies to the person. */
  since: string;
}

/** One reset of a daily count: a person's, or the whole org's (user_id null). */
export interface CopilotQuotaReset {
  id: string;
  user_id: string | null;
  email: string;
  display_name: string;
  reset_at: string;
  /** The email of the platform admin who reset it; "" once that user is gone. */
  reset_by: string;
  note: string;
}

/** A suite product as a platform admin sees it for one org (admin.OrgSuiteProduct, migration 0227). */
export interface AdminOrgSuiteProduct {
  key: string;
  name: string;
  /** Whether the org holds it; the fields below are set only when it does. */
  held: boolean;
  status?: "trial" | "active" | "suspended";
  plan?: string;
  seats?: number | null;
  since?: string;
  until?: string | null;
  usable: boolean;
  updated_at?: string;
}

/** An org's opt-in to its data training shared models, and who set or cleared it last, and when. */
export interface AdminModelTraining {
  opt_in: boolean;
  at?: string;
  by?: string;
}

/** GET /v1/admin/orgs/{org_id}/products: every suite product, the org's entitlement to each, and the opt-in. */
export interface AdminOrgProducts {
  org_id: string;
  products: AdminOrgSuiteProduct[];
  model_training: AdminModelTraining;
}

/** PUT /v1/admin/orgs/{org_id}/products/{product}: replaces the entitlement; a field left out is cleared. */
export interface AdminOrgSuiteProductPut {
  status: "trial" | "active" | "suspended";
  plan?: string;
  seats?: number | null;
  until?: string | null;
}

/** An org's daily limits for the consultant, its usage and its latest resets (twin.CopilotLimitsView). */
export interface CopilotLimits {
  org_id: string;
  user_daily: CopilotLimit;
  org_daily: CopilotLimit;
  /** The org's messages counted against its limit, since org_since (a day ago, or its latest whole-org reset). */
  org_count: number;
  org_since: string;
  /** The people with messages counted, most first (at most 10). */
  people: CopilotUsage[];
  /** The latest resets, newest first (at most 10). */
  resets: CopilotQuotaReset[];
  now: string;
}

/** PUT /v1/admin/orgs/{org_id}/copilot-limits: both limits, each a number from 1 to 100000 or null for the platform's. */
export interface CopilotLimitsPut {
  user_daily: number | null;
  org_daily: number | null;
}

/** POST /v1/admin/orgs/{org_id}/copilot-limits/reset: a member's count, or with no user_id the whole org's. */
export interface CopilotQuotaResetRequest {
  user_id?: string | null;
  note?: string;
}

/**
 * POST /v1/admin/orgs/{org_id}/copy-setup: what a copy of another org's setup wrote into the target, by kind; what it
 * skipped, each with why; and what a copy never takes (internal/twin/org_copy_setup.go).
 */
export interface OrgCopySetupSummary {
  source_org_id: string;
  target_org_id: string;
  plants: number;
  /** The pieces of equipment the copy ADDED to the target. */
  entities: number;
  /** The pieces of equipment the target already had and kept, which the copy's bindings now point at. */
  entities_matched: number;
  /** The connections the copy ADDED to the target. */
  connections: number;
  /** The connections the target already had. */
  connections_matched: number;
  data_sources: number;
  extractors: number;
  extractor_versions: number;
  streams: number;
  process_windows: number;
  scrap_targets: number;
  vocab_bindings: number;
  /** The org's own defect types a word binding may name (migration 0265); absent from an older server. */
  defect_types?: number;
  products: number;
  plant_time: boolean;
  skipped: string[];
  not_copied: string[];
}

/** Mirrors admin.UserSummary. */
export interface UserSummary {
  id: string;
  email: string;
  display_name: string;
  created_at: string;
  is_platform_admin: boolean;
  is_disabled: boolean;
  org_count: number;
  // Sign-up approval gate. Populated per-user by the admin god-view so the
  // platform console can list + action pending access requests. Optional so
  // older API builds (pre-gate) still type-check.
  approval_status?: "pending" | "approved" | "rejected";
  approval_submitted?: boolean;
  applicant_name?: string;
  applicant_company?: string;
  applicant_position?: string;
  applicant_phone?: string;
}

/** One org a user belongs to + their role in it. Mirrors admin.UserOrgMembership.
 *  Lazy-loaded when a user row is expanded in the console (#580). */
export interface UserOrgMembership {
  org_id: string;
  name: string;
  slug: string;
  role: string;
  is_owner: boolean;
}

export const platformAdminApi = {
  /** Every org on the install — paged in full (server caps ?limit at 200) so the
   *  console shows ALL orgs, not just the newest 50 (the 84-org / 50-row bug). */
  orgs: () => fetchAllPages<OrgSummary>("/v1/admin/orgs"),
  /** Every user on the install — paged in full for the same reason as `orgs`. */
  users: () => fetchAllPages<UserSummary>("/v1/admin/users"),
  /** The orgs a given user belongs to + their role in each (owners first).
   *  Lazy-loaded on user-row expand (#580); the base users list carries only a
   *  count. Membership sets are small, so a single un-paged fetch is fine. */
  userOrgs: (userId: string) =>
    api
      .get<Paginated<UserOrgMembership>>(`/v1/admin/users/${userId}/orgs`)
      .then((r) => r.items),

  // Lifecycle controls (Phase 1). Each flips a Postgres-authoritative
  // flag enforced fail-closed at orgctx + AuthMe.
  setOrgDisabled: (orgId: string, disabled: boolean) =>
    api.post<{ id: string; is_disabled: boolean }>(
      `/v1/admin/orgs/${orgId}/${disabled ? "disable" : "enable"}`,
      {},
    ),
  setUserDisabled: (userId: string, disabled: boolean) =>
    api.post<{ id: string; is_disabled: boolean }>(
      `/v1/admin/users/${userId}/${disabled ? "disable" : "enable"}`,
      {},
    ),
  /**
   * Sign-up approval actions (super-admin only). Approve flips the user's
   * approval_status to 'approved' (they pass the RequireApproved gate on their
   * next /auth/me); reject flips it to 'rejected' (→ /access-denied). No body;
   * server reads the target from the path. Surfaced in the /app/platform
   * "Access requests" section.
   */
  approveUser: (userId: string) =>
    api.post<{ id: string; approval_status: "approved" }>(
      `/v1/admin/users/${userId}/approve`,
      {},
    ),
  rejectUser: (userId: string) =>
    api.post<{ id: string; approval_status: "rejected" }>(
      `/v1/admin/users/${userId}/reject`,
      {},
    ),
  /** Toggle the foundry pack (digital twin) for an org. */
  setOrgTwin: (orgId: string, enabled: boolean) =>
    api.post<{ org_id: string; pack: string; enabled: boolean }>(
      `/v1/admin/orgs/${orgId}/packs/foundry/${enabled ? "enable" : "disable"}`,
      {},
    ),
  /**
   * Switch an org's genealogy model — flips its latest plant.source onto the
   * new mass-conserving / time-stamped emitter ("newmodel" → "new") or back to
   * today's legacy emitter ("legacy"). Replaces the old hidden localStorage
   * opt-in with a real super-admin control. 409 ("no_provisioned_plant") if the
   * org has no plant yet.
   */
  setOrgGenealogyModel: (orgId: string, model: "newmodel" | "legacy") =>
    api.post<{ org_id: string; genealogy_model: "new" | "legacy" }>(
      `/v1/admin/orgs/${orgId}/genealogy-model/${model === "newmodel" ? "new" : "legacy"}`,
      {},
    ),
  /**
   * Toggle the per-org physics twin (org_settings.physics_twin_enabled,
   * migration 0076) — POST enables, DELETE disables. Gates the S2 live
   * physics tick, the reconcile worker and /v1/twin/simcore/*. Freely
   * reversible; default OFF, so existing orgs are untouched until opted in.
   */
  setOrgPhysicsTwin: (orgId: string, enabled: boolean) =>
    enabled
      ? api.post<{ org_id: string; physics_twin_enabled: boolean }>(
          `/v1/admin/orgs/${orgId}/physics-twin`,
          {},
        )
      : api.delete<{ org_id: string; physics_twin_enabled: boolean }>(
          `/v1/admin/orgs/${orgId}/physics-twin`,
        ),
  /**
   * Show or hide the analysis plan view for an org (org_settings.analysis_plan_view, migration 0169): POST
   * shows the Plan tab of the correlations page, DELETE hides it. Freely reversible; default OFF.
   */
  setOrgAnalysisPlanView: (orgId: string, enabled: boolean) =>
    enabled
      ? api.post<{ org_id: string; analysis_plan_view: boolean }>(`/v1/admin/orgs/${orgId}/analysis-plan-view`, {})
      : api.delete<{ org_id: string; analysis_plan_view: boolean }>(`/v1/admin/orgs/${orgId}/analysis-plan-view`),
  /**
   * Show or hide the knowledge store to an org's members (org_settings.knowledge_customer_visible, migration
   * 0156, plant-analytics B-6b): POST opens the Knowledge page to them (confirmed findings marked for customers),
   * DELETE hides it again. Freely reversible; default OFF.
   */
  setOrgKnowledgeVisible: (orgId: string, enabled: boolean) =>
    enabled
      ? api.post<{ org_id: string; knowledge_customer_visible: boolean }>(`/v1/admin/orgs/${orgId}/knowledge-visible`, {})
      : api.delete<{ org_id: string; knowledge_customer_visible: boolean }>(`/v1/admin/orgs/${orgId}/knowledge-visible`),
  /**
   * The knowledge switch and what the org's members see with it on, by the customer view's own rules, and what the
   * Sahas team still holds back (GET /v1/admin/orgs/{org_id}/knowledge-visible): the console and the Knowledge page
   * say it before the plant meets an empty page.
   */
  getOrgKnowledgeView: (orgId: string) =>
    api.get<KnowledgeCustomerView>(`/v1/admin/orgs/${orgId}/knowledge-visible`),
  /**
   * Let an org's readers revise themselves when a file they matched fails on them (org_settings.auto_revise_readers,
   * migration 0260): POST turns it on, DELETE off. A platform admin still approves each next version. Default OFF.
   */
  setOrgAutoReviseReaders: (orgId: string, enabled: boolean) =>
    enabled
      ? api.post<{ org_id: string; auto_revise_readers: boolean }>(`/v1/admin/orgs/${orgId}/auto-revise-readers`, {})
      : api.delete<{ org_id: string; auto_revise_readers: boolean }>(`/v1/admin/orgs/${orgId}/auto-revise-readers`),
  /**
   * The platform's gate for the org's improvement projects (org_settings.improvement_projects_allowed, migration 0200):
   * POST opens it, so the org's admins may turn projects on (Admin, Packs); DELETE shuts it again (its projects are
   * hidden and kept). Freely reversible; default shut.
   */
  setOrgImprovementProjects: (orgId: string, enabled: boolean) =>
    enabled
      ? api.post<{ org_id: string; improvement_projects_allowed: boolean }>(`/v1/admin/orgs/${orgId}/improvement-projects`, {})
      : api.delete<{ org_id: string; improvement_projects_allowed: boolean }>(`/v1/admin/orgs/${orgId}/improvement-projects`),
  /** The suite products and the org's entitlement to each, with its model training opt-in (migration 0227). */
  getOrgProducts: (orgId: string) => api.get<AdminOrgProducts>(`/v1/admin/orgs/${orgId}/products`),
  /** Grant a product, or change how the org holds it; the body replaces the entitlement. */
  putOrgProduct: (orgId: string, product: string, body: AdminOrgSuiteProductPut) =>
    api.put<unknown>(`/v1/admin/orgs/${orgId}/products/${product}`, body),
  /** The org no longer holds the product; its members' roles in it go too. Idempotent. */
  deleteOrgProduct: (orgId: string, product: string) => api.delete<void>(`/v1/admin/orgs/${orgId}/products/${product}`),
  /** Record the org's written agreement to its data training shared models, or clear it. */
  setOrgModelTraining: (orgId: string, optIn: boolean) =>
    optIn
      ? api.post<{ org_id: string; model_training_opt_in: boolean }>(`/v1/admin/orgs/${orgId}/model-training`, {})
      : api.delete<{ org_id: string; model_training_opt_in: boolean }>(`/v1/admin/orgs/${orgId}/model-training`),
  /**
   * The org's daily limits for the AI Casting Consultant (migration 0201): each with where it comes from, the org's
   * count, the top people's, and the latest resets.
   */
  getOrgCopilotLimits: (orgId: string) => api.get<CopilotLimits>(`/v1/admin/orgs/${orgId}/copilot-limits`),
  /** Set the org's own limits; null clears one back to the platform's. Answers the org's view. */
  putOrgCopilotLimits: (orgId: string, body: CopilotLimitsPut) =>
    api.put<CopilotLimits>(`/v1/admin/orgs/${orgId}/copilot-limits`, body),
  /**
   * Start a count again: a member's (user_id), or with none the whole org's. A reset never deletes a message. Answers
   * the org's view, the new reset first.
   */
  resetOrgCopilotQuota: (orgId: string, body: CopilotQuotaResetRequest) =>
    api.post<CopilotLimits>(`/v1/admin/orgs/${orgId}/copilot-limits/reset`, body),
  /**
   * Copy the source org's setup into an empty real org (the target): the plant model, plant time, data sources,
   * active extractors with their streams, windows in force, word bindings and products, under new ids. No readings,
   * heats, lots, findings, threads or members: the plant's files are imported into the target again after it.
   */
  copyOrgSetup: (targetOrgId: string, sourceOrgId: string) =>
    api.post<OrgCopySetupSummary>(`/v1/admin/orgs/${targetOrgId}/copy-setup`, { source_org_id: sourceOrgId }),
  /**
   * Toggle the per-org demo simulator (org_settings.simulator_enabled,
   * migration 0092, #581) — POST re-enables, DELETE freezes. When frozen the
   * simulator tick writes no new readings for this org only (the per-org
   * alternative to the global SIMULATOR_DISABLED env flag). Opt-OUT: default
   * ON, so existing orgs keep simulating until explicitly frozen here.
   */
  setOrgSimulator: (orgId: string, enabled: boolean) =>
    enabled
      ? api.post<{ org_id: string; simulator_enabled: boolean }>(
          `/v1/admin/orgs/${orgId}/simulator`,
          {},
        )
      : api.delete<{ org_id: string; simulator_enabled: boolean }>(
          `/v1/admin/orgs/${orgId}/simulator`,
        ),
  /**
   * Reclassify an org's equipment as REAL (the dummy→real toggle) — flips
   * every Thing's things.is_simulated=false AND simulate=false (real gear
   * never self-simulates). The data persists; the sim stops. Returns the new
   * data_class + how many Things were touched. The console reflects the new
   * data_class on its org row so the Real/Sim badge updates.
   */
  markOrgReal: (orgId: string) =>
    api.post<{ id: string; data_class: "real"; things_updated: number }>(
      `/v1/admin/orgs/${orgId}/mark-real`,
      {},
    ),
  /**
   * Reclassify an org's equipment as SIMULATED (demo) — flips every Thing's
   * things.is_simulated=true so its readings become eligible for the daily
   * sim-cleanup. Guarded: returns 409 { error: "org_has_real_data" } if the
   * org holds imported/vendor equipment — a real customer — so a real
   * tenant's data can never be turned into purgeable junk by a mis-click.
   */
  markOrgSimulated: (orgId: string) =>
    api.post<{ id: string; data_class: "simulated"; things_updated: number }>(
      `/v1/admin/orgs/${orgId}/mark-simulated`,
      {},
    ),
  /**
   * Super-admin hard-deletes ANY org — `DELETE /v1/admin/orgs/{org_id}` (same
   * /v1/admin god-view group as the org list + disable/enable above).
   * Cascades all of that org's data. 200. Gated server-side by
   * RequirePlatformAdmin; surfaced only in the /app/platform console. The
   * console invalidates its org list after.
   */
  deleteOrg: (orgId: string) =>
    api.delete<void>(`/v1/admin/orgs/${orgId}`),
};

// ── Mock-device factory (Phase 2) ─────────────────────────────────────────

/** A tag inside a blueprint (preview of what minting will create). */
export interface FactoryBlueprintTag {
  name: string;
  unit: string;
  data_kind: string;
  semantic_tag: string;
  kind: string; // sim profile kind: const|sine|ramp|random_walk|noise
}

/** A mintable mock-device source type. Mirrors admin.blueprintSummary. */
export interface FactoryBlueprint {
  id: string;
  label: string;
  description: string;
  device_count: number;
  tags: FactoryBlueprintTag[];
}

/** A minted simulated source. Mirrors admin.SourceSummary. */
export interface FactorySource {
  id: string;
  slug: string;
  name: string;
  created_at: string;
  claimed: boolean;
  owner_org?: string;
  device_count: number;
  tag_count: number;
  /**
   * Whether a live (unconsumed, unexpired) claim code exists for this source.
   * The plaintext code is NO LONGER returned in the list — claim codes are
   * hashed at rest (#229), so the code is revealed exactly once at mint
   * (MintResult) or via `factoryApi.rotateToken` (re-mint). Replaces the
   * former `claim_code` field.
   */
  has_active_code: boolean;
}

/** The fresh claim code returned by a re-mint (admin RotateToken). */
export interface RotateTokenResult {
  claim_code: string;
  gateway_id: string;
  expires_at: string;
}

export interface MintResult {
  id: string;
  slug: string;
  name: string;
  blueprint: string;
  device_count: number;
  tag_count: number;
  claim_code: string;
  expires_at: string;
}

export const factoryApi = {
  blueprints: () =>
    api.get<{ blueprints: FactoryBlueprint[] }>("/v1/admin/factory/blueprints"),
  sources: () => api.get<Paginated<FactorySource>>("/v1/admin/factory/sources"),
  mint: (blueprint: string, name?: string) =>
    api.post<MintResult>("/v1/admin/factory/sources", { blueprint, name }),
  /**
   * Re-mint a source's claim code (admin RotateToken). Voids any prior active
   * code and returns a fresh plaintext code ONCE — codes are hashed at rest
   * (#229) and cannot be re-displayed in the list. A factory source's id is
   * its gateway id.
   */
  rotateToken: (id: string) =>
    api.post<RotateTokenResult>(`/v1/admin/gateways/${id}/rotate-token`, {}),
  remove: (id: string) =>
    api.delete<{ id: string; purged: boolean }>(`/v1/admin/factory/sources/${id}`),
  /**
   * Bulk-purge ALL is_simulated (demo) Things for an org — readings, child
   * Things, tags + bindings cascade. Real tenant-added Things are untouched.
   * Super-admin only (RequirePlatformAdmin). Returns the count purged.
   */
  purgeOrgSimulated: (orgId: string) =>
    api.post<{ org_id: string; purged: number }>(
      `/v1/admin/factory/orgs/${orgId}/purge-simulated`,
      {},
    ),
};
